import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'react-query';

import classNames from 'classnames';
import Modal from 'react-bootstrap/Modal';

import FilledButton from '../../components/Buttons/FilledButton';

import usericon from '../../assets/images/usericon.webp';
import getUserPhotoUrl from '../../helpers/getUserPhotoUrl';
import classes from './styles.module.scss';
import AnswerService from '../../services/AnswerService';
import Option from '../../components/Option';
import Checkbox from '../../components/Checkbox';
import EmojiPickerWithButton from '../../components/EmojiPickerWithButton';
import UserService from '../../services/UserService';
import useCheckMobileScreen from '../../hooks/useCheckMobileScreen';
import { useMeetingViewStore } from '../../store';

export default function QuestionModal({
  show,
  handleClose,
  onAnswerSubmitted,
  refetchMeeting,
  preview,
  shouldDisableQuestionModalAnimationOnEnter,
  shouldDisableQuestionModalAnimationOnExit,
  existingAnswer, // New prop: existing answer to prefill
}) {
  const [noAnimation, setNoAnimation] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [answerText, setAnswerText] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]);

  const currentQuestion =
    useMeetingViewStore((state) => state.currentQuestion) || {};

  const isMobile = useCheckMobileScreen();

  const { data: user } = useQuery('me', UserService.getMe);

  const { mutate: createAnswer } = useMutation(AnswerService.createAnswer, {
    onSuccess: () => {
      refetchMeeting();
      setIsSubmitted(true);
      // Mark section as handled immediately when answer is submitted
      // This prevents the modal from re-showing during the 5-second delay
      // Pass the answer text and isUpdate flag (false for new answers)
      if (onAnswerSubmitted) {
        const submittedText = answerText || selectedOptions.join(', ');
        onAnswerSubmitted(submittedText, false); // false indicates this is a new answer
      }
      setTimeout(() => {
        handleClose();
      }, 5000);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const { mutate: updateAnswer } = useMutation(AnswerService.updateAnswer, {
    onSuccess: () => {
      refetchMeeting();
      setIsSubmitted(true);
      // Mark section as handled immediately when answer is updated
      // Pass the answer text and isUpdate flag to update the asset
      if (onAnswerSubmitted) {
        const submittedText = answerText || selectedOptions.join(', ');
        onAnswerSubmitted(submittedText, true); // true indicates this is an update
      }
      setTimeout(() => {
        handleClose();
      }, 5000);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  // Initialize with existing answer when modal opens
  useEffect(() => {
    if (show && existingAnswer) {
      const answerValue = existingAnswer.text || '';
      setAnswerText(answerValue);
      
      // For single/multiple choice, parse the answer text
      if (currentQuestion.options?.type === 'Single Choice' || currentQuestion.options?.type === 'Multiple Choice') {
        // Split by comma and trim for multiple choice
        const options = answerValue.split(',').map((opt) => opt.trim()).filter(Boolean);
        setSelectedOptions(options);
      }
    } else if (!show) {
      setTimeout(() => {
        setIsSubmitted(false);
        setAnswerText('');
        setSelectedOptions([]);
      }, 150);
    }
  }, [show, existingAnswer, currentQuestion.options]);

  const isSubmitDisabled = !answerText.trim().length && !selectedOptions.length;

  const submitAnswer = () => {
    if (!isSubmitDisabled) {
      const answerTextValue = answerText || selectedOptions.join(', ');
      
      // If there's an existing answer, update it; otherwise create a new one
      if (existingAnswer?.id) {
        updateAnswer({
          answerId: existingAnswer.id,
          text: answerTextValue,
        });
      } else {
      createAnswer({
        questionId: currentQuestion.id,
          text: answerTextValue,
      });
      }
    }
  };

  const handleSelectMultipleChoice = (option) => {
    if (selectedOptions.includes(option)) {
      setSelectedOptions((currentOptions) =>
        currentOptions.filter((currentOption) => currentOption !== option)
      );
    } else {
      setSelectedOptions((currentOptions) => [...currentOptions, option]);
    }
  };

  let answer = (
    <div className={classes.form}>
      <div className={classes.userpic}>
        <img
          src={getUserPhotoUrl(user?.photoPath) || usericon}
          alt="User"
        />
        <p className={classes.username}>{user?.name}</p>
      </div>
      <textarea
        value={answerText}
        onChange={(event) => setAnswerText(event.target.value)}
        placeholder="Start typing..."
      />
      <EmojiPickerWithButton
        setCommentText={setAnswerText}
        buttonBottom="24px"
        buttonRight="24px"
        pickerTop="52px"
      />
    </div>
  );

  if (currentQuestion.options?.type === 'Single Choice') {
    answer = (
      <ul className={classes.radioButtons}>
        {currentQuestion.options?.options.map((option) => (
          <Option
            key={option}
            isChecked={selectedOptions.includes(option)}
            onClick={() => setSelectedOptions([option])}
          >
            {option}
          </Option>
        ))}
      </ul>
    );
  }

  if (currentQuestion.options?.type === 'Multiple Choice') {
    answer = (
      <ul className={classes.multipleChoice}>
        {currentQuestion.options?.options.map((option) => (
          <Checkbox
            key={option}
            isChecked={selectedOptions.includes(option)}
            onClick={() => handleSelectMultipleChoice(option)}
            label={option}
            big={!isMobile}
            labelStyle={{ fontSize: isMobile ? 18 : 24 }}
          />
        ))}
      </ul>
    );
  }

  const isAnswered = !!currentQuestion?.Answers?.find(
    (answr) => answr.User.id === user?.id
  );

  const isPossibleToClose =
    (currentQuestion?.isAnswerRequired && isAnswered) ||
    preview ||
    !currentQuestion?.isAnswerRequired;

  let buttons = (
    <div
      className={classNames(
        classes.buttons,
        currentQuestion?.options && classes.multipleChoice
      )}
    >
      <FilledButton
        type="button"
        className={classes.submitButton}
        onClick={submitAnswer}
        isDisabled={isSubmitDisabled}
        width={192}
      >
        Submit
      </FilledButton>
      {(isAnswered || !currentQuestion.isAnswerRequired) && (
        <FilledButton
          type="button"
          className={classes.skipButton}
          onClick={handleClose}
          color="black"
          width={133}
        >
          Skip
        </FilledButton>
      )}
    </div>
  );

  if (currentQuestion?.isAnswerRequired) {
    buttons = (
      <>
        <div
          className={classNames(
            classes.buttons,
            currentQuestion?.options && classes.multipleChoice
          )}
        >
          <FilledButton
            type="button"
            className={classes.submitButton}
            onClick={submitAnswer}
            disabled={isSubmitDisabled}
          >
            Submit
          </FilledButton>
          {(isAnswered || !currentQuestion.isAnswerRequired) && (
            <FilledButton
              type="button"
              className={classes.skipButton}
              onClick={handleClose}
              color="black"
            >
              Skip
            </FilledButton>
          )}
        </div>
        <p className={classes.requiredInfo}>
          *This question requires an answer in order to move on through the
          recorded video.
        </p>
      </>
    );
  }

  if (currentQuestion?.options) {
    buttons = (
      <>
        <div
          className={classNames(
            classes.buttons,
            classes.multipleChoiceButtonsContainer
          )}
        >
          <FilledButton
            type="button"
            className={classes.submitButton}
            onClick={submitAnswer}
            isDisabled={isSubmitDisabled}
            width={192}
          >
            Submit
          </FilledButton>
          {(isAnswered || !currentQuestion.isAnswerRequired) && (
            <FilledButton
              type="button"
              className={classes.skipButton}
              onClick={handleClose}
              width={133}
              color="black"
            >
              Skip
            </FilledButton>
          )}
        </div>
        {currentQuestion.isAnswerRequired && (
          <p className={classes.requiredInfo}>
            *This question requires an answer in order to move on through the
            recorded video.
          </p>
        )}
      </>
    );
  }

  if (preview && currentQuestion?.options) {
    buttons = (
      <div className={classNames(classes.buttons, classes.multipleChoice)}>
        <FilledButton
          type="button"
          className={classes.submitButton}
          onClick={submitAnswer}
          isDisabled
          width={192}
        >
          Submit
        </FilledButton>

        <FilledButton
          type="button"
          className={classes.skipButton}
          onClick={handleClose}
          color="black"
        >
          Skip
        </FilledButton>

        <div className={classes.messageContainer}>
          <p>You are in preview mode</p>
          <p>Click Skip to finishing reviewing your video</p>
        </div>
      </div>
    );
  }

  if (preview && !currentQuestion?.options) {
    buttons = (
      <div className={classNames(classes.buttons)}>
        <div className={classes.messageContainer}>
          <p>You are in preview mode</p>
          <p>Click Skip to finishing reviewing your video</p>
        </div>
        <FilledButton
          type="button"
          className={classes.submitButton}
          onClick={submitAnswer}
          isDisabled
          width={192}
        >
          Submit
        </FilledButton>

        <FilledButton
          type="button"
          className={classes.skipButton}
          onClick={handleClose}
          color="black"
        >
          Skip
        </FilledButton>
      </div>
    );
  }

  return (
    <Modal
      onExit={() => setNoAnimation(shouldDisableQuestionModalAnimationOnExit)}
      onEnter={() => setNoAnimation(shouldDisableQuestionModalAnimationOnEnter)}
      animation={!noAnimation}
      show={show}
      centered
      contentClassName={classNames(classes.QuestionModal, {
        [classes.submitted]: isSubmitted,
        [classes.multipleChoice]: !!currentQuestion?.options,
      })}
      dialogClassName={classNames(classes.dialog, {
        [classes.submitted]: isSubmitted,
        [classes.multipleChoice]: !!currentQuestion?.options,
      })}
      backdropClassName={classes.backdrop}
    >
      {(isPossibleToClose || isSubmitted) && (
        <i
          className={classes.closeIcon}
          onClick={() => handleClose({ shouldContinue: false })}
        >
          Close
        </i>
      )}
      {isSubmitted ? (
        <div className={classes.submitMessage}>
          <p className={classes.title}>Response Submitted!</p>
          <p>
            Your response has been saved below under the Question Responses tab
          </p>
        </div>
      ) : (
        <>
          <p
            className={classes.question}
            //   style={{ textAlign: question?.options ? 'left' : 'center' }}
          >
            {currentQuestion?.text}
          </p>
          {answer}
          {buttons}
        </>
      )}
    </Modal>
  );
}
